import React, { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';

const C = {
  primary: '#0F6E56', primaryBg: '#E1F5EE', border: '#E3E1D9',
  text: '#1F1E1B', muted: '#5F5E5A', surface: '#F7F5F0',
  gold: '#91600C', danger: '#C0392B', purple: '#9B59B6',
};

function authHeaders() {
  const t = localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Token ${t}` } : {}) };
}

const inp = {
  width: '100%', padding: '9px 11px', fontSize: 13.5,
  border: `1px solid ${C.border}`, borderRadius: 7,
  boxSizing: 'border-box', marginBottom: 9,
};

/**
 * Broadcast - sab users ko ek sath paigham.
 * Bhejne se pehle audience ka andaza dikhata hai.
 */
export default function BroadcastPanel() {
  const [scope, setScope] = useState('all');
  const [value, setValue] = useState('');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [withDm, setWithDm] = useState(true);
  const [aud, setAud] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const loadAudience = useCallback(() => {
    const q = new URLSearchParams({ scope });
    if (value) q.set('value', value);
    fetch(`/api/broadcast/audience/?${q}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j && !j.error) setAud(j); })
      .catch(() => {});
  }, [scope, value]);

  useEffect(() => { loadAudience(); }, [loadAudience]);

  const send = async () => {
    if (message.trim().length < 5) { setMsg('Please write a longer message.'); return; }
    if (!aud || !aud.count) { setMsg('Nobody matches this audience.'); return; }

    if (!window.confirm(
      `Send this to ${aud.count} ${aud.count === 1 ? 'person' : 'people'}?\n\n` +
      `"${message.slice(0, 120)}"\n\n` +
      `${withDm ? 'Bell + Discord/Telegram DM' : 'Bell only'}\n\nThis cannot be undone.`
    )) return;

    setBusy(true); setMsg('');
    try {
      const res = await fetch('/api/broadcast/send/', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ message, link, scope, value, with_dm: withDm }),
      });
      const j = await safeJson(res);
      if (!res.ok) throw new Error(j.error || 'Could not send.');
      setMsg(`Sending to ${j.queued} people. Bells appear right away; DMs take a little longer.`);
      setMessage(''); setLink('');
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };

  const scopeBtn = (k, label) => (
    <button key={k}
      onClick={() => { setScope(k); setValue(''); }}
      style={{
        fontSize: 12, padding: '6px 12px', borderRadius: 7, cursor: 'pointer',
        border: `1px solid ${scope === k ? C.primary : C.border}`,
        background: scope === k ? C.primaryBg : '#fff',
        color: scope === k ? C.primary : C.muted,
        fontWeight: scope === k ? 700 : 400,
      }}>
      {label}
    </button>
  );

  return (
    <>
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: 13, marginBottom: 12 }}>
        <p style={{ margin: '0 0 3px', fontSize: 14, fontWeight: 700 }}>
          {'\u{1F4E2}'} Send a message to your community
        </p>
        <p style={{ margin: '0 0 11px', fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          Goes out as a bell notification, and as a Discord/Telegram DM to anyone who linked their account.
        </p>

        {/* ---- audience ---- */}
        <p style={{ margin: '0 0 6px', fontSize: 11.5, fontWeight: 700, color: C.muted }}>WHO GETS IT</p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 9 }}>
          {scopeBtn('all', 'Everyone')}
          {scopeBtn('country', 'By country')}
          {scopeBtn('module', 'By activity')}
          {scopeBtn('online', 'Active this week')}
        </div>

        {scope === 'country' && aud && (
          <select value={value} onChange={(e) => setValue(e.target.value)} style={inp}>
            <option value="">Choose a country…</option>
            {(aud.countries || []).map((c) => (
              <option key={c.code} value={c.code}>{c.code} — {c.count} members</option>
            ))}
          </select>
        )}

        {scope === 'module' && aud && (
          <select value={value} onChange={(e) => setValue(e.target.value)} style={inp}>
            <option value="">Choose a group…</option>
            {(aud.modules || []).map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        )}

        {/* ---- kitne logon tak ---- */}
        {aud && (
          <div style={{
            background: aud.count ? C.primaryBg : '#FDEDEC',
            border: `1px solid ${aud.count ? C.primary : C.danger}`,
            borderRadius: 8, padding: '8px 11px', marginBottom: 11,
          }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: aud.count ? C.primary : C.danger }}>
              {aud.count} of {aud.total_users} members
            </p>
            {aud.sample && aud.sample.length > 0 && (
              <p style={{ margin: '3px 0 0', fontSize: 11, color: C.muted }}>
                {aud.sample.map((u) => '@' + u).join(', ')}
                {aud.count > aud.sample.length ? ` and ${aud.count - aud.sample.length} more` : ''}
              </p>
            )}
          </div>
        )}

        {/* ---- paigham ---- */}
        <p style={{ margin: '0 0 6px', fontSize: 11.5, fontWeight: 700, color: C.muted }}>MESSAGE</p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          maxLength={900}
          placeholder="What do you want to tell them? Keep it short and clear."
          style={{ ...inp, resize: 'vertical' }}
        />
        <p style={{ margin: '-4px 0 9px', fontSize: 10.5, color: message.length > 800 ? C.danger : C.muted, textAlign: 'right' }}>
          {message.length} / 900
        </p>

        <input
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="Link when they tap it (optional) — e.g. /forum"
          style={inp}
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginBottom: 11, cursor: 'pointer' }}>
          <input type="checkbox" checked={withDm} onChange={(e) => setWithDm(e.target.checked)} />
          <span>Also send a Discord / Telegram DM</span>
        </label>

        {msg && (
          <p style={{ fontSize: 12, margin: '0 0 9px',
                      color: msg.startsWith('Sending') ? C.primary : C.danger }}>
            {msg}
          </p>
        )}

        <button
          onClick={send}
          disabled={busy || !aud || !aud.count || message.trim().length < 5}
          style={{
            fontSize: 13.5, fontWeight: 700, padding: '10px 22px', border: 'none',
            borderRadius: 8, color: '#fff',
            background: (busy || !aud || !aud.count || message.trim().length < 5) ? '#B4B2A9' : C.purple,
            cursor: busy ? 'wait' : 'pointer',
          }}>
          {busy ? 'Sending…' : `\u{1F4E2} Send to ${aud ? aud.count : 0}`}
        </button>
      </div>

      <div style={{ background: C.surface, borderRadius: 9, padding: '10px 13px' }}>
        <p style={{ margin: '0 0 5px', fontSize: 12, fontWeight: 700 }}>Before you send</p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: C.muted, lineHeight: 1.7 }}>
          <li>It cannot be taken back once sent.</li>
          <li>Say what it is about in the first few words — that is all they see in the bell.</li>
          <li>Too many broadcasts and people stop reading them.</li>
          <li>For one module only, use <b>By activity</b> instead of sending to everyone.</li>
        </ul>
      </div>
    </>
  );
}
